import React, { useRef, useState } from 'react';
import StlModelViewer from './StlModelViewer';

function resolvePublicAssetPath(assetPath) {
  if (!assetPath) return '';
  if (/^https?:\/\//i.test(assetPath)) return assetPath;

  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${assetPath.startsWith('/') ? assetPath : `/${assetPath}`}`;
}

export default function ProjectsPage({ page, isDark, onThemeToggle, onHome }) {
  const items = Object.entries(page.items);
  const [expandedItems, setExpandedItems] = useState({});
  const galleryRefs = useRef({});

  const toggleDescription = (slug) => {
    setExpandedItems((prev) => ({
      ...prev,
      [slug]: !prev[slug],
    }));
  };

  const scrollGallery = (slug, direction) => {
    galleryRefs.current[slug]?.scrollBy({
      left: direction * galleryRefs.current[slug].clientWidth,
      behavior: 'smooth',
    });
  };

  return (
    <div className="projects-page">
      <div className="projects-grid-bg" />

      <nav className="navbar">
        <button className="nav-logo nav-logo-button" onClick={onHome}>
          S_
        </button>
        <div className="nav-right">
          <button className="projects-home-button" onClick={onHome}>
            ~/portfolio
          </button>
          <button className="theme-toggle" onClick={onThemeToggle}>
            {isDark ? '☀' : '☾'}
          </button>
        </div>
      </nav>

      <main className="projects-main">
        <button className="directory-back-button" onClick={onHome} aria-label="Back to terminal">
          &lt;
        </button>
        <p className="projects-kicker">{page.path}</p>
        <h1 className="projects-title">{page.title}</h1>
        <p className="projects-intro">{page.intro}</p>

        <section className="projects-list" aria-label={`${page.title} list`}>
          {items.map(([slug, project]) => {
            const shouldShowModel = page.showModels && project.showModel !== false;
            const hasVisual = shouldShowModel || project.simulationGif || project.logo || project.images?.length;
            const tags = project.tech || [];
            const links = [
              project.paperLink && project.paperLink !== '#' && {
                href: project.paperLink,
                label: project.paperLinkLabel || 'View Paper',
              },
              project.link && project.link !== '#' && { href: project.link, label: 'View Project' },
            ].filter(Boolean);

            return (
              <article className={`project-card ${hasVisual ? '' : 'project-card-text-only'}`} key={slug}>
                {shouldShowModel && (
                  <div className="project-model">
                    <StlModelViewer
                      modelPath={project.modelPath}
                      modelRotation={project.modelRotation}
                      modelScale={project.modelScale}
                      label={project.title}
                    />
                  </div>
                )}

                {!page.showModels && project.simulationGif && (
                  <div className="project-media">
                    <img
                      src={resolvePublicAssetPath(project.simulationGif)}
                      alt={project.simulationGifAlt || `${project.title} simulation`}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {!page.showModels && !project.simulationGif && (project.logo || project.images?.length > 0) && (
                  <div
                    className={`project-logo ${page.itemPathPrefix === 'hobbies' ? 'project-logo-hobby' : ''} ${project.images?.length > 1 ? 'project-gallery' : ''}`}
                  >
                    <div
                      className={project.images?.length > 1 ? 'project-gallery-track' : ''}
                      ref={(element) => {
                        if (project.images?.length > 1) galleryRefs.current[slug] = element;
                      }}
                    >
                      {project.images?.length > 0 ? project.images.map((imagePath, imageIndex) => (
                        <img
                          key={imagePath}
                          src={resolvePublicAssetPath(imagePath)}
                          alt={`${project.title} photo ${imageIndex + 1}`}
                        />
                      )) : (
                        <img
                          src={resolvePublicAssetPath(project.logo)}
                          alt={project.logoAlt || `${project.title} logo`}
                        />
                      )}
                    </div>
                    {project.images?.length > 1 && (
                      <>
                        <button
                          className="gallery-arrow gallery-arrow-left"
                          type="button"
                          onClick={() => scrollGallery(slug, -1)}
                          aria-label={`Previous ${project.title} photo`}
                        >
                          &lt;
                        </button>
                        <button
                          className="gallery-arrow gallery-arrow-right"
                          type="button"
                          onClick={() => scrollGallery(slug, 1)}
                          aria-label={`Next ${project.title} photo`}
                        >
                          &gt;
                        </button>
                      </>
                    )}
                  </div>
                )}

                <div className="project-copy">
                  <div className="project-path">{page.itemPathPrefix}/{slug}.txt</div>
                  <h2>{project.title}</h2>
                  <p className={`project-description ${expandedItems[slug] ? 'project-description-expanded' : ''}`}>
                    {project.description}
                  </p>
                  <button
                    className="project-more-button"
                    onClick={() => toggleDescription(slug)}
                    aria-expanded={Boolean(expandedItems[slug])}
                  >
                    {expandedItems[slug] ? 'Less' : 'More'}
                  </button>

                  {tags.length > 0 && (
                    <div className="project-tags">
                      {tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  )}

                  {links.length > 0 && (
                    <div className="project-links">
                      {links.map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          className="project-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      </main>
    </div>
  );
}
